// eslint-disable-next-line import/no-extraneous-dependencies
import React, { FC, PropsWithChildren, useEffect } from "react";
import { useSetSettings, useSigma } from "@react-sigma/core";
// eslint-disable-next-line import/no-extraneous-dependencies
import { Attributes } from "graphology-types";

import { drawHover, drawLabel } from "./canvas-utils";
import useDebounce from "./use-debounce";



/* Documentation, see:
1. Core: https://graphology.github.io/
2. Model Layer: https://www.sigmajs.org/ 
3. Example: -> underlying src for demo on home page: 
    https://github.com/jacomyal/sigma.js/tree/23e53736c9a95bd0bc81bdef9644be11e6d630c2/packages/demo/src/views
4. React layer: https://github.com/sim51/react-sigma
*/

const NODE_FADE_COLOR = "#bbb";
const EDGE_FADE_COLOR = "#eee";
const NODE_HIGHLIGHT_COLOR = '#153362';
const EDGE_HIGHLIGHT_COLOR = '#0d6efd';

const GraphSettingsController: FC<PropsWithChildren<{ hoveredNode: string | null }>> = ({ children, hoveredNode }) => {
  const sigma = useSigma();
  const setSettings = useSetSettings();
  const graph = sigma.getGraph();

  // Here we debounce the value to avoid having too much highlights refresh when
  // moving the mouse over the graph:
  const debouncedHoveredNode = useDebounce(hoveredNode, 40);

  /**
   * Initialize here settings that require to know the graph and/or the sigma
   * instance:
   */
  useEffect(() => {
    // eslint-disable-next-line unused-imports/no-unused-vars
    const hoveredColor: string = (debouncedHoveredNode && sigma.getNodeDisplayData(debouncedHoveredNode)?.color) || "";

    setSettings({
      defaultDrawNodeLabel: drawLabel,
      defaultDrawNodeHover: drawHover,
      nodeReducer: (node: string, data: Attributes) => {
        if (debouncedHoveredNode) {
          return node === debouncedHoveredNode ||
            graph.hasEdge(node, debouncedHoveredNode) ||
            graph.hasEdge(debouncedHoveredNode, node)
            ? { ...data, zIndex: 1, color: NODE_HIGHLIGHT_COLOR }
            : { ...data, zIndex: 0, label: "", color: NODE_FADE_COLOR, image: null, highlighted: false };
        }
        return data;
      },
      edgeReducer: (edge: string, data: Attributes) => {
        if (debouncedHoveredNode) {
          return graph.hasExtremity(edge, debouncedHoveredNode)
            ? { ...data, color: EDGE_HIGHLIGHT_COLOR, size: 4 }
            : { ...data, color: EDGE_FADE_COLOR, hidden: true };
        }
        return data;
      },
    });
  }, [sigma, graph, debouncedHoveredNode]);

  /**
   * Update node and edge reducers when a node is hovered, to highlight its
   * neighborhood:
   */
  useEffect(() => {
    const hoveredColor: string = (debouncedHoveredNode && sigma.getNodeDisplayData(debouncedHoveredNode)?.color) || "";

    sigma.setSetting(
      "nodeReducer",
      debouncedHoveredNode
        ? (node, data) =>
            node === debouncedHoveredNode ||
            graph.hasEdge(node, debouncedHoveredNode) ||
            graph.hasEdge(debouncedHoveredNode, node)
              ? { ...data, zIndex: 1 }
              : { ...data, zIndex: 0, label: "", color: NODE_FADE_COLOR, image: null, highlighted: false }
        : null,
    );
    sigma.setSetting(
      "edgeReducer",
      debouncedHoveredNode
        ? (edge, data) =>
            graph.hasExtremity(edge, debouncedHoveredNode)
              ? { ...data, color: hoveredColor, size: 4 }
              : { ...data, color: EDGE_FADE_COLOR, hidden: true }
        : null,
    );
  }, [debouncedHoveredNode]);

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

export default GraphSettingsController;